import React, { ComponentPropsWithoutRef } from "react";

import classNames from "classnames";
import {
  FieldValues,
  Path,
  UseFormRegister,
  UseFormReturn,
} from "react-hook-form";
import { twMerge } from "tailwind-merge";

import Icon from "../Icons";
import { HelperText, ErrorText, Label } from "./FormCopy";

export interface SelectOption {
  value: string | number | undefined | null;
  label: string | null;
}
export type selectInputProps = ComponentPropsWithoutRef<"select">;

export const SelectField = <T extends FieldValues>({
  formProps,
  label,
  helpText,
  errorMessage,
  options,
  ...props
}: {
  formProps?: Pick<
    UseFormReturn<T>,
    "formState" | "getFieldState" | "register"
  >;
  label: React.ReactNode;
  helpText?: React.ReactNode;
  errorMessage?: React.ReactNode;
  options?: SelectOption[];
  name: Path<T>;
  onChange?: (newValue: React.ChangeEvent<HTMLSelectElement>) => void;
} & selectInputProps) => {
  const error = formProps?.getFieldState(
    props.name,
    formProps?.formState,
  ).error;
  return (
    <div className="space-y-12">
      {(label || helpText) && (
        <div>
          <Label htmlFor={undefined}>{label}</Label>
          <HelperText> {helpText}</HelperText>
        </div>
      )}
      <div className="space-y-4">
        <SelectInput
          children={undefined}
          formProps={formProps}
          options={options}
          {...props}
        />
        <ErrorText inError={!!error}>{error?.message}</ErrorText>
      </div>
    </div>
  );
};

export const SelectInput = <T extends FieldValues>({
  options,
  formProps,
  children,
  className,
  ...props
}: {
  options?: SelectOption[];
  formProps?: Pick<UseFormReturn<T>, "register">;
  children?: React.ReactNode;
  className?: string;
  name?: Path<T>;
} & selectInputProps) => {
  if (!children && !options) {
    console.warn(
      "SelectInput component must have either options or children prop",
    );
  }
  return (
    <div className="relative">
      <select
        className={twMerge(
          "block w-full appearance-none rounded border border-neutral-200 bg-white py-8 pl-16 pr-32 leading-normal focus:outline-none focus:ring",
          "disabled:bg-neutral-100 disabled:text-neutral-400",
          className,
        )}
        {...(formProps?.register && props.name
          ? formProps.register(props.name)
          : {})}
        {...props}
      >
        {children ||
          (options || []).map(({ label, value }) => (
            <option
              key={value || "emptyState"}
              value={value == null ? undefined : value}
            >
              {label}
            </option>
          ))}
      </select>

      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral-600">
        <Icon
          size="xl"
          key="expandMore"
          icon="expandMore"
          color="current"
          className={undefined}
        />
      </div>
    </div>
  );
};

SelectInput.Unstyled = <T extends FieldValues>({
  options,
  formProps: { register },
  className,
  children,
  name,
  ...props
}: selectInputProps & {
  options?: SelectOption[];
  formProps: Pick<UseFormReturn<T>, "register">;
  name?: Path<T>;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
}) => {
  return (
    <div className="relative h-full">
      <select
        className={twMerge("block w-full appearance-none pr-16", className)}
        {...(name ? register(name) : {})}
        {...props}
      >
        {children ||
          (options || []).map(({ label, value }) => (
            <option key={value} value={value || undefined}>
              {label}
            </option>
          ))}
      </select>

      {!props.disabled && (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
          <Icon size="xl" key="expandMore" icon="expandMore" color="current" />
        </div>
      )}
    </div>
  );
};

export default SelectField;
